import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { useAuth } from '../context/AuthContext';
import { fetchEditingRequests, updateRequestStatus, fetchEditors, assignEditor, addProjectNote } from '../graphqlService';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import StatusChip from './ui/StatusChip';
import LoadingState from './ui/LoadingState';
import EmptyState from './ui/EmptyState';
import PriorityFlag from './Projects/PriorityFlag';
import { AssignEditorDialog, CancelProjectDialog, MessageEditorDialog } from './Projects/ProjectDialogs';
import {
  EditorOption,
  PROJECT_FILTERS,
  ProjectFilter,
  ProjectObject,
  countByFilter,
  friendlyError,
  getAllowedActions,
  parseFilter,
  projectEditor,
  projectHasEditor,
  revisionUsage,
  visibleProjects,
} from './Projects/projectListModel';
import { brand, clickableCardSx, statusTone } from '../theme';
import { debugLog } from '../utils/debugLog';
import { formatDate, formatDateTime, pluralize } from '../utils/format';

const LOAD_ERROR = "We couldn't load your projects. Check your connection and try again.";
const TABPANEL_ID = 'projects-tabpanel';

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const DateText: React.FC<{ value?: string }> = ({ value }) => {
  const text = formatDate(value);
  if (!text) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Typography
      component="time"
      dateTime={value}
      title={formatDateTime(value)}
      variant="body2"
      color="text.secondary"
      sx={{ whiteSpace: 'nowrap' }}
    >
      {text}
    </Typography>
  );
};

const RevisionText: React.FC<{ project: ProjectObject }> = ({ project }) => {
  const usage = revisionUsage(project);
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color: usage.atLimit ? statusTone.attention.fg : 'text.secondary',
        fontWeight: usage.atLimit ? 600 : 400,
      }}
    >
      {usage.label}
      {usage.atLimit && <Box component="span" sx={visuallyHidden}> (limit reached)</Box>}
    </Typography>
  );
};

const MyProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  // Below md the table becomes stacked cards: a sideways-scrolling table hid
  // the actions column, the only way into a project.
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseFilter(searchParams.get('status'));

  const [projects, setProjects] = useState<ProjectObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [editors, setEditors] = useState<EditorOption[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const showSnackbar = (message: string) => setSnackbar({ open: true, message });

  // Row menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuProject, setMenuProject] = useState<ProjectObject | null>(null);

  // Cancel
  const [cancelTarget, setCancelTarget] = useState<ProjectObject | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Assign an editor. The optional note goes into the assignment email the
  // server sends (which carries the project link) and onto the project.
  const [assignTarget, setAssignTarget] = useState<ProjectObject | null>(null);
  const [selectedEditorId, setSelectedEditorId] = useState('');
  const [editorMessage, setEditorMessage] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Message the editor
  const [messageTarget, setMessageTarget] = useState<ProjectObject | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const token = await user?.getIdToken(true);
        if (token) {
          const fetchedProjects = await fetchEditingRequests(1, 50, token);
          if (cancelled) return;
          // An empty list is a normal state, not an error.
          setProjects(fetchedProjects.requests || []);

          try {
            const fetchedEditors = await fetchEditors();
            if (!cancelled) {
              setEditors(fetchedEditors.filter((editor: any) => (editor.status || '').toLowerCase() === 'active'));
            }
          } catch (error) {
            console.error('Failed to load editors:', error);
          }
        }
      } catch (error) {
        // An outage must not read as "you have no projects".
        console.error('Failed to load data:', error);
        if (!cancelled) {
          setProjects([]);
          setLoadError(LOAD_ERROR);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [user, reloadKey]);

  const counts = useMemo(() => countByFilter(projects), [projects]);
  const shownProjects = useMemo(() => visibleProjects(projects, filter, searchTerm), [projects, filter, searchTerm]);
  const activeFilter = PROJECT_FILTERS.find((f) => f.value === filter) ?? PROJECT_FILTERS[0];

  const setFilter = (next: ProjectFilter) => {
    setSearchParams(next === 'all' ? {} : { status: next }, { replace: true });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilter('all');
  };

  const refreshProjects = async (token: string) => {
    const fetchedProjects = await fetchEditingRequests(1, 50, token);
    setProjects(fetchedProjects.requests || []);
  };

  // ---- Row actions --------------------------------------------------------------

  const openProject = (project: ProjectObject) => navigate(`/project-details/${project.id}`);

  /**
   * The whole row opens the project. Clicks on its own links and buttons, on
   * anything rendered in a portal (the menu, dialogs) and text selection don't.
   */
  const handleRowClick = (event: React.MouseEvent<HTMLElement>, project: ProjectObject) => {
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return;
    if (target.closest('a, button, input, textarea, select, [role="button"], [role="menuitem"]')) return;
    if (window.getSelection?.()?.toString()) return;
    if (event.metaKey || event.ctrlKey) {
      window.open(`/project-details/${project.id}`, '_blank', 'noopener');
      return;
    }
    openProject(project);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, project: ProjectObject) => {
    setMenuAnchor(event.currentTarget);
    setMenuProject(project);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const handleCancelClick = (project: ProjectObject) => {
    setCancelError(null);
    setCancelTarget(project);
  };

  const handleAssignProject = (id: string) => {
    const project = projects.find(p => p.id === id);
    const userRole = user?.role || 'unknown';
    const hasEditor = project ? projectHasEditor(project) : false;
    const isAdmin = userRole === 'superadmin';
    const isProjectOwner = project?.userId === user?.uid;
    const hasPermission = !hasEditor && (isAdmin || (isProjectOwner && project?.status === 'VIDEO_SUBMITTED'));

    debugLog('Assign editor action:', {
      projectId: id,
      userRole,
      hasEditor,
      isAdmin,
      isProjectOwner,
      projectStatus: project?.status,
      hasPermission,
      timestamp: new Date().toISOString(),
      action: 'assign_editor'
    });

    // Block unauthorized access
    if (!hasPermission || !project) {
      console.warn('BLOCKED: User attempted to assign editor without proper permissions');
      setActionError('Only the project owner can assign an editor, and only before one is assigned.');
      return;
    }

    setSelectedEditorId('');
    setEditorMessage('');
    setAssignError(null);
    setAssignTarget(project);
  };

  const handleCancelConfirm = async () => {
    if (cancelling || !cancelTarget) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const token = await user?.getIdToken(true);
      if (token) {
        // Cancelled, not deleted: the project keeps its history and the
        // editor is told.
        await updateRequestStatus(
          cancelTarget.id,
          'CANCELLED',
          'Project cancelled by user',
          null,
          null,
          token
        );

        try {
          await refreshProjects(token);
        } catch (refreshError) {
          console.error('Cancelled, but the list did not refresh:', refreshError);
          setProjects(prev => prev.map(p => (p.id === cancelTarget.id ? { ...p, status: 'CANCELLED' } : p)));
        }

        setCancelTarget(null);
        showSnackbar('Project cancelled');
      }
    } catch (error) {
      console.error('Error cancelling project:', error);
      setCancelError(friendlyError(error, "We couldn't cancel the project. Try again."));
    } finally {
      setCancelling(false);
    }
  };

  const handleAssignConfirm = async () => {
    if (assigning || !assignTarget) return;
    setAssignError(null);
    setAssigning(true);
    try {
      const token = await user?.getIdToken(true);
      if (token && selectedEditorId) {
        const selectedEditor = editors.find(e => e.id === selectedEditorId);
        if (!selectedEditor) {
          setAssignError('Choose an editor.');
          return;
        }

        // The server emails the editor; the note travels with the assignment.
        await assignEditor(assignTarget.id, selectedEditorId, token, editorMessage);

        try {
          await refreshProjects(token);
        } catch (refreshError) {
          console.error('Assigned, but the list did not refresh:', refreshError);
          setProjects(prev => prev.map(p => (p.id === assignTarget.id
            ? { ...p, editorId: selectedEditor.id, editorName: selectedEditor.name, editorEmail: selectedEditor.email, status: 'EDITOR_ASSIGNED' }
            : p)));
        }

        setAssignTarget(null);
        setSelectedEditorId('');
        setEditorMessage('');
        showSnackbar('Editor assigned and notified');
      }
    } catch (error) {
      console.error('Error assigning editor:', error);
      setAssignError(friendlyError(error, "We couldn't assign the editor. Try again."));
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignCancel = () => {
    setAssignTarget(null);
    setSelectedEditorId('');
    setEditorMessage('');
    setAssignError(null);
  };

  const handleMessageEditor = (project: ProjectObject) => {
    const userRole = user?.role || 'unknown';
    const hasEditor = !!project.editorEmail;
    const isProjectOwner = project.userId === user?.uid;

    debugLog('Message editor action:', {
      projectId: project.id,
      userRole,
      hasEditor,
      isProjectOwner,
      editorEmail: project.editorEmail || 'none',
      hasPermission: hasEditor && user?.uid,
      timestamp: new Date().toISOString(),
      action: 'message_editor'
    });

    // The email template already names the sender and the project, so the
    // draft is just the message itself.
    setMessageText('');
    setMessageError(null);
    setMessageTarget(project);
  };

  const handleSendMessage = async () => {
    if (!messageTarget || !messageText.trim()) {
      setMessageError('Write a message first.');
      return;
    }
    if (sendingMessage) return;
    setSendingMessage(true);
    setMessageError(null);

    try {
      const token = await user?.getIdToken(true);
      if (token && messageTarget.editorEmail) {
        // A project note is the message: the server stores it and emails the
        // editor through its template. Sending a second, raw email from the
        // client as well meant the editor received every message twice.
        await addProjectNote(messageTarget.id, messageText.trim(), 'GENERAL', token);

        setMessageTarget(null);
        setMessageText('');
        showSnackbar('Message sent to the editor');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageError(friendlyError(error, "We couldn't send your message. Try again."));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMessageCancel = () => {
    setMessageTarget(null);
    setMessageText('');
    setMessageError(null);
  };

  // ---- Pieces of a row ---------------------------------------------------------------

  const nameLink = (project: ProjectObject) => (
    <Link
      component={RouterLink}
      to={`/project-details/${project.id}`}
      underline="hover"
      sx={{
        fontWeight: 600,
        color: 'text.primary',
        overflowWrap: 'anywhere',
        '&:hover': { color: brand.copperDark },
        '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2, borderRadius: '2px' },
      }}
    >
      {project.projectName || 'Untitled project'}
    </Link>
  );

  const editorCell = (project: ProjectObject) => {
    const editor = projectEditor(project);
    const actions = getAllowedActions(project, user);
    if (editor.name || editor.email) {
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {editor.name || editor.email}
          </Typography>
          {editor.name && editor.email && (
            <Typography variant="caption" color="text.secondary" noWrap component="p">
              {editor.email}
            </Typography>
          )}
        </Box>
      );
    }
    if (editor.id) {
      return <Typography variant="body2" color="text.secondary">Assigned</Typography>;
    }
    if (actions.canAssignEditor) {
      return (
        <Button
          size="small"
          variant="text"
          startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => handleAssignProject(project.id)}
          sx={{ ml: -1 }}
        >
          Assign editor
        </Button>
      );
    }
    return <Typography variant="body2" color="text.secondary">Not assigned</Typography>;
  };

  const menuButton = (project: ProjectObject) => {
    const open = Boolean(menuAnchor) && menuProject?.id === project.id;
    return (
      <IconButton
        size="small"
        aria-label={`More actions for ${project.projectName || 'this project'}`}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'project-actions-menu' : undefined}
        onClick={(event) => openMenu(event, project)}
      >
        <MoreVertOutlinedIcon fontSize="small" />
      </IconButton>
    );
  };

  const menuItems = (() => {
    if (!menuProject) return [];
    const project = menuProject;
    const actions = getAllowedActions(project, user);
    const items: React.ReactElement[] = [
      <MenuItem key="open" onClick={() => { closeMenu(); openProject(project); }}>
        <ListItemIcon><ArrowForwardOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Open project</ListItemText>
      </MenuItem>,
    ];
    // Messaging needs an editor email on the project.
    if (project.editorEmail && user?.uid) {
      items.push(
        <MenuItem key="message" onClick={() => { closeMenu(); handleMessageEditor(project); }}>
          <ListItemIcon><ChatBubbleOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Message editor</ListItemText>
        </MenuItem>
      );
    }
    // No "Edit draft": a submitted brief can't be edited, and the old edit
    // route only ever opened a second review page (it now redirects to
    // Project details, which "Open project" already reaches).
    if (!projectHasEditor(project) && actions.canAssignEditor) {
      items.push(
        <MenuItem key="assign" onClick={() => { closeMenu(); handleAssignProject(project.id); }}>
          <ListItemIcon><PersonAddAltOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign editor</ListItemText>
        </MenuItem>
      );
    }
    if (actions.canCancel) {
      items.push(<Divider key="divider" />);
      items.push(
        <MenuItem key="cancel" onClick={() => { closeMenu(); handleCancelClick(project); }} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}><BlockOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Cancel project</ListItemText>
        </MenuItem>
      );
    }
    return items;
  })();

  // ---- Content ------------------------------------------------------------------------

  const renderTable = () => (
    <TableContainer>
      <Table aria-label="Projects">
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Editor</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Revisions</TableCell>
            <TableCell align="right" sx={{ width: 56 }}>
              <Box component="span" sx={visuallyHidden}>Actions</Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shownProjects.map((project) => (
            <TableRow
              key={project.id}
              data-testid={`project-row-${project.id}`}
              onClick={(event) => handleRowClick(event, project)}
              sx={{ cursor: 'pointer', '&:last-of-type td': { borderBottom: 0 } }}
            >
              <TableCell sx={{ maxWidth: 360, py: 1.5 }}>
                {nameLink(project)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mt: 0.25 }}>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {project.videoName}
                  </Typography>
                  <PriorityFlag priority={project.priority} />
                </Box>
              </TableCell>
              <TableCell>
                <StatusChip kind="project" status={project.status} />
              </TableCell>
              <TableCell sx={{ maxWidth: 220 }}>{editorCell(project)}</TableCell>
              <TableCell><DateText value={project.createdAt} /></TableCell>
              <TableCell><DateText value={project.updatedAt} /></TableCell>
              <TableCell align="right"><RevisionText project={project} /></TableCell>
              <TableCell align="right" sx={{ pl: 0 }}>{menuButton(project)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCards = () => (
    <Box component="ul" aria-label="Projects" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
      {shownProjects.map((project) => (
        <Card
          component="li"
          key={project.id}
          data-testid={`project-row-${project.id}`}
          onClick={(event: React.MouseEvent<HTMLElement>) => handleRowClick(event, project)}
          sx={{ ...clickableCardSx, p: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {nameLink(project)}
              <Typography variant="body2" color="text.secondary" noWrap>
                {project.videoName}
              </Typography>
            </Box>
            <Box sx={{ mt: -0.5, mr: -1 }}>{menuButton(project)}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.25 }}>
            <StatusChip kind="project" status={project.status} />
            <PriorityFlag priority={project.priority} />
          </Box>
          <Box
            component="dl"
            sx={{ m: 0, mt: 1.5, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 2, rowGap: 0.75, alignItems: 'center' }}
          >
            <Typography component="dt" variant="body2" color="text.secondary">Editor</Typography>
            <Box component="dd" sx={{ m: 0, minWidth: 0 }}>{editorCell(project)}</Box>
            <Typography component="dt" variant="body2" color="text.secondary">Updated</Typography>
            <Box component="dd" sx={{ m: 0 }}><DateText value={project.updatedAt || project.createdAt} /></Box>
            <Typography component="dt" variant="body2" color="text.secondary">Revisions</Typography>
            <Box component="dd" sx={{ m: 0 }}><RevisionText project={project} /></Box>
          </Box>
        </Card>
      ))}
    </Box>
  );

  const renderContent = () => {
    if (loadError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      );
    }
    if (loading) {
      return <LoadingState variant="table" count={5} label="Loading projects" />;
    }
    if (projects.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={<FolderOpenOutlinedIcon />}
            title="No projects yet"
            description="A project sends an annotated video and your brief to an editor. Your projects and where they stand show up here."
            secondaryActionLabel="Create your first project"
            onSecondaryAction={() => navigate('/submit-editing-request')}
          />
        </Card>
      );
    }
    if (shownProjects.length === 0) {
      const term = searchTerm.trim();
      return (
        <Card>
          <EmptyState
            compact
            icon={<SearchOffOutlinedIcon />}
            title={term ? `No projects match “${term}”` : activeFilter.empty}
            description={
              term
                ? filter === 'all'
                  ? 'Try another name, or clear the search.'
                  : `Nothing in ${activeFilter.label.toLowerCase()} matches. Try All, or clear the search.`
                : 'Projects show up here when they reach this stage.'
            }
            secondaryActionLabel="Show all projects"
            onSecondaryAction={clearFilters}
          />
        </Card>
      );
    }
    return compact ? renderCards() : renderTable();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Projects"
        subtitle="Everything you've sent to an editor, and where it stands."
        action={
          <Button component={RouterLink} to="/submit-editing-request" variant="contained" startIcon={<AddOutlinedIcon />}>
            New project
          </Button>
        }
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Filters and search */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-end' },
          gap: { xs: 1.5, md: 2 },
          mb: 2.5,
          borderBottom: { md: `1px solid ${brand.border}` },
        }}
      >
        <Tabs
          value={filter}
          onChange={(_, next: ProjectFilter) => setFilter(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Filter projects by status"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 44,
            // A colour in the same value: a responsive `borderBottom: 1` resets
            // the colour inside its media query and drew the line in ink.
            borderBottom: { xs: `1px solid ${brand.border}`, md: 'none' },
            '& .MuiTab-root': { color: 'text.secondary' },
            '& .MuiTab-root.Mui-selected': { color: 'text.primary' },
            // The arrow that can't scroll takes no room, so the first tab
            // lines up with the content edge.
            '& .MuiTabs-scrollButtons.Mui-disabled': { width: 0, overflow: 'hidden' },
          }}
        >
          {PROJECT_FILTERS.map((option) => (
            <Tab
              key={option.value}
              value={option.value}
              id={`projects-tab-${option.value}`}
              aria-controls={TABPANEL_ID}
              aria-label={loading || loadError ? option.label : `${option.label}, ${pluralize(counts[option.value], 'project')}`}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {option.label}
                  {!loading && !loadError && (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 20,
                        px: 0.75,
                        borderRadius: '10px',
                        backgroundColor: 'grey.200',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        lineHeight: '20px',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {counts[option.value]}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <TextField
          type="search"
          size="small"
          placeholder="Search projects"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ 'aria-label': 'Search projects' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, mb: { md: 1 } }}
        />
      </Box>

      <Box role="tabpanel" id={TABPANEL_ID} aria-labelledby={`projects-tab-${filter}`}>
        {renderContent()}
      </Box>

      <Menu
        id="project-actions-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': menuProject ? `Actions for ${menuProject.projectName || 'this project'}` : 'Project actions' }}
      >
        {menuItems}
      </Menu>

      <CancelProjectDialog
        open={Boolean(cancelTarget)}
        projectName={cancelTarget?.projectName}
        cancelling={cancelling}
        error={cancelError}
        onConfirm={handleCancelConfirm}
        onClose={() => {
          setCancelTarget(null);
          setCancelError(null);
        }}
      />

      <AssignEditorDialog
        open={Boolean(assignTarget)}
        projectName={assignTarget?.projectName}
        editors={editors}
        editorId={selectedEditorId}
        onEditorChange={setSelectedEditorId}
        note={editorMessage}
        onNoteChange={setEditorMessage}
        assigning={assigning}
        error={assignError}
        onConfirm={handleAssignConfirm}
        onClose={handleAssignCancel}
      />

      <MessageEditorDialog
        open={Boolean(messageTarget)}
        projectName={messageTarget?.projectName}
        editorName={messageTarget?.editorName}
        editorEmail={messageTarget?.editorEmail}
        message={messageText}
        onMessageChange={setMessageText}
        sending={sendingMessage}
        error={messageError}
        onSend={handleSendMessage}
        onClose={handleMessageCancel}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </PageContainer>
  );
};

export default MyProjects;
